import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { adminDiagnosticsApi, type DiagnosticAssignmentRow, type QuizSetSummary } from '@/api/adminDiagnostics'
import { ModalShell } from './ModalShell'

interface Props {
  source: DiagnosticAssignmentRow
  studentName: string
  ageSets: QuizSetSummary[]
  gradeSets: QuizSetSummary[]
  onClose: () => void
  onDone: () => void
}

export function RetakeModal({ source, studentName, ageSets, gradeSets, onClose, onDone }: Props) {
  const { t } = useTranslation('admin')
  const [aiq, setAiq] = useState(true)
  const [level, setLevel] = useState(false)
  const [aiqSetId, setAiqSetId] = useState<number>(source.aiqQuizSetId)
  const [levelSetId, setLevelSetId] = useState<number>(source.levelQuizSetId)

  const retake = useMutation({
    mutationFn: () =>
      adminDiagnosticsApi.retake(source.id, {
        aiq,
        level,
        aiqQuizSetId: aiq ? aiqSetId : undefined,
        levelQuizSetId: level ? levelSetId : undefined,
      }),
    onSuccess: onDone,
  })

  const canSubmit = (aiq || level) && !retake.isPending

  return (
    <ModalShell title={t('diagnostic.retake.title', { name: studentName, round: source.roundNo + 1 })} onClose={onClose}>
      <p className="text-sm text-gray-600 mb-3">{t('diagnostic.retake.carryNote')}</p>

      <label className="flex items-center gap-2 mb-2 text-sm">
        <input data-testid="seg-aiq" type="checkbox" checked={aiq} onChange={(e) => setAiq(e.target.checked)} />
        <span className="font-medium text-gray-800">{t('diagnostic.retake.aiqSegment')}</span>
      </label>
      {aiq && (
        <label className="block mb-3 ml-6">
          <span className="text-sm text-gray-600 mb-1 block">{t('diagnostic.retake.aiqSetLabel')}</span>
          <select value={aiqSetId} onChange={(e) => setAiqSetId(Number(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
            {ageSets.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      )}

      <label className="flex items-center gap-2 mb-2 text-sm">
        <input data-testid="seg-level" type="checkbox" checked={level} onChange={(e) => setLevel(e.target.checked)} />
        <span className="font-medium text-gray-800">{t('diagnostic.retake.levelSegment')}</span>
      </label>
      {level && (
        <label className="block mb-3 ml-6">
          <span className="text-sm text-gray-600 mb-1 block">{t('diagnostic.retake.levelSetLabel')}</span>
          <select value={levelSetId} onChange={(e) => setLevelSetId(Number(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
            {gradeSets.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      )}

      {retake.isError && <p className="text-sm text-red-600 mb-2">{t('diagnostic.retake.failed')}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-sm">
          {t('diagnostic.retake.close')}
        </button>
        <button data-testid="retake-submit" type="button" disabled={!canSubmit} onClick={() => retake.mutate()}
          className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm disabled:opacity-50">
          {t('diagnostic.retake.submit')}
        </button>
      </div>
    </ModalShell>
  )
}
